import Image from "next/image";
import { useIntl, FormattedMessage, IntlShape } from "react-intl";

import { Button } from "@/components/atoms/Button";
import { Text } from "@/components/atoms/Text";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from "@/components/molecules/Tooltip";
import { Breakpoints } from "@/foundations/breakpoints/breakpoints";
import PowerHostFilled from "@/icons/__generated__/PowerHostFilled/PowerHostFilled20";
import { useWindowSize } from "@/lib/hooks/use-window-size";
import { useListingData } from "~/context/ListingPageDataContext";
import { useTransactable } from "~/hooks/use-transactable";
import useRedirectToSigninSignup from "~/hooks/useRedirectToSigninSignup";
import { AppModalKey, useAppModal } from "~/modals";

import { useAiAnswersEnabled } from "./ai-instant-answers";

const HostAvatar = ({
  ownerAvatar,
  isPowerHost,
  intl,
}: {
  ownerAvatar: string;
  isPowerHost: boolean;
  intl: IntlShape;
}) => {
  return (
    <div className="tw-relative">
      <div className="tw-relative tw-size-48 tqw-mr-0 lg:tw-mr-8 tw-mb-4 lg:tw-mb-0 group-has-[.tw-flex-col]:max-lg:tw-size-80">
        <Image
          src={ownerAvatar || ""}
          fill
          className="tw-rounded-circular"
          alt="avatar"
          unoptimized
        />
      </div>
      {isPowerHost && (
        <div className="tw-absolute tw-bottom-0 tw-right-0 tw-bg-brand-white tw-rounded-full tw-p-2 tw-shadow-md">
          <PowerHostFilled
            className="tw-text-purple-800"
            aria-label={intl.formatMessage({
              id: "listing.listing_main.power_host_alt",
              defaultMessage: "Power host badge",
            })}
          />
        </div>
      )}
    </div>
  );
};

const PowerHostTooltip = ({ section }: { section?: "top" }) => {
  const windowDims = useWindowSize();
  const isLgWindow = windowDims.width && windowDims.width >= Breakpoints.LG;

  return (
    <Tooltip
      rootProps={{
        delayDuration: 0,
      }}
    >
      <TooltipTrigger className="tw-flex tw-items-center tw-gap-2">
        <Text typeface="body-1" className="tw-text-gray-600">
          <FormattedMessage
            id="listing.listing_main.power_host"
            defaultMessage="Power Host"
          />
        </Text>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        align={isLgWindow || section === "top" ? "start" : "center"}
        alignOffset={isLgWindow || section === "top" ? -36 : 0}
        className="tw-max-w-[280px] tw-text-gray-900 tw-py-12 tw-px-16 tw-text-left tw-body-1-long tw-items-start"
      >
        <div>
          <FormattedMessage
            id="listing.tooltip.power_host"
            defaultMessage="Power Hosts meet the highest standard for hosting on Peerspace. {link}"
            values={{
              link: (
                <a
                  href="http://try.peerspace.com/power-host/"
                  rel="noopener noreferrer"
                  target="_blank"
                  className="tw-cursor-pointer tw-body-1-underline"
                >
                  <FormattedMessage
                    id="listing.tooltip.learn_more"
                    defaultMessage="Learn More"
                  />
                </a>
              ),
            }}
          />
        </div>
        <TooltipArrow className="tw-fill-base-white" />
      </TooltipContent>
    </Tooltip>
  );
};

export const HostDetails = () => {
  const intl = useIntl();
  const { data: listingData } = useListingData();
  const {
    listingId,
    ownerAvatar,
    ownerName,
    isPowerHost,
    listingOwnerId,
    fullService,
    listingStatus,
  } = listingData;
  const { checkTransactable } = useTransactable();
  const { handleRedirectToSignupPage, isUserLoggedIn } =
    useRedirectToSigninSignup();
  const aiAnswersEnabled = useAiAnswersEnabled(listingId ?? "");
  const { openModal: openAiAnswersModal } = useAppModal(
    AppModalKey.ListingAiAnswersModal,
  );
  const isListingPaused =
    listingStatus === "PAUSED" || listingStatus === "PAUSED_INTERNALLY";

  const handleMessageHost = () => {
    // if the user is logged in - allow messaging
    // if the user is logged out - force signup by adding signup to urlSearchParams
    const url = `/inquiry/${listingId}`;

    if (!isUserLoggedIn) {
      handleRedirectToSignupPage("msg", url);
    } else {
      checkTransactable(() => {
        window.location.href = url;
        return;
      });
    }
  };

  const handleOpenAiAnswers = () => {
    openAiAnswersModal({
      listingData,
      listingId,
      onMessageHost: handleMessageHost,
    });
  };

  return (
    <div className="tw-py-24 lg:tw-border-b-[1px] lg:tw-border-gray-300 lg:tw-flex tw-justify-between tw-items-center tw-text-center">
      <div className="tw-group tw-flex tw-w-full tw-flex-col lg:tw-flex-row tw-items-center lg:tw-gap-12 tw-gap-24">
        <HostAvatar
          ownerAvatar={ownerAvatar!}
          isPowerHost={isPowerHost!}
          intl={intl}
        />
        <div className="tw-flex tw-flex-col max-lg:tw-items-center tw-gap-4">
          <Text
            typeface="headline-2-m-underline"
            className="tw-cursor-pointer lg:tw-body-2-m-underline"
            data-testing-id="hostedByLabel"
            element="a"
            aria-label={`View ${ownerName}'s profile`}
            href={`/profiles/${listingOwnerId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {fullService?.active ? (
              <FormattedMessage
                defaultMessage="Venue contact: {ownerName}"
                values={{
                  ownerName: ownerName,
                }}
              />
            ) : (
              <FormattedMessage
                defaultMessage="Hosted by {ownerName}"
                values={{
                  ownerName: ownerName,
                }}
              />
            )}
          </Text>
          {isPowerHost && <PowerHostTooltip />}
        </div>
        {!isListingPaused && (
          <div className="lg:tw-ml-auto">
            {aiAnswersEnabled ? (
              <Button
                appearance="white"
                variant="outline-2"
                className="tw-px-16 tw-py-8 tw-mt-4 lg:tw-mt-0"
                data-testing-id="haveAQuestionButton"
                onClick={handleOpenAiAnswers}
              >
                <FormattedMessage defaultMessage="Message host" />
              </Button>
            ) : (
              <Button
                appearance="white"
                variant="outline-2"
                className="tw-px-16 tw-py-8 tw-mt-4 lg:tw-mt-0"
                data-testing-id="messageHostButton"
                onClick={handleMessageHost}
              >
                {fullService?.active ? (
                  <FormattedMessage defaultMessage="Message venue" />
                ) : (
                  <FormattedMessage defaultMessage="Message host" />
                )}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
